/**
 * Phase 5.9.10 — Bulk actions dropdown (ORDERS tab cleanup tools).
 *
 * Replaces the two inline buttons that used to live above the orders table:
 *   - DELETE N CANCELLED  (red — purge cancelled orders)
 *   - DELETE OLD DELIVERED  (red — delete delivered orders >7 days old; PERMANENT, no archive/restore)
 *
 * Now tucked behind a single `⋯ BULK ACTIONS` dropdown so the toolbar stays
 * clean. Dropdown is hidden entirely when there is nothing to clean up
 * (cancelled=0 AND deliveredOld<=5).
 *
 * Requires feature flag `deleteOrderEnabled=true` — otherwise the entire
 * trigger is hidden (matches prior behavior).
 */
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

export function BulkActionsMenu({
  enabled = false,
  cancelledCount = 0,
  deliveredCount = 0,
  onDeleteCancelled,
  onDeleteOldDelivered,
}) {
  if (!enabled) return null;
  if (cancelledCount === 0 && deliveredCount <= 5) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#FF9F0A] hover:text-[#FF9F0A] rounded-none font-mono text-xs h-8 px-2"
          data-testid="bulk-actions-trigger"
          aria-label="Bulk cleanup actions"
        >
          <MoreHorizontal className="w-3.5 h-3.5 mr-1" />
          BULK ACTIONS
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#0C0612] border border-[#2A0F3D] text-[#A3C7A8] font-mono min-w-[240px] rounded-none"
        data-testid="bulk-actions-content"
      >
        <DropdownMenuLabel className="text-[#FF9F0A] uppercase text-[10px] tracking-wider">
          // Cleanup
        </DropdownMenuLabel>
        {cancelledCount > 0 && (
          <DropdownMenuItem
            onSelect={() => onDeleteCancelled?.()}
            className="cursor-pointer hover:bg-[rgba(255,59,48,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5 text-[#FF3B30]"
            data-testid="bulk-delete-cancelled"
          >
            <Trash2 className="w-3.5 h-3.5" />
            DELETE {cancelledCount} CANCELLED
          </DropdownMenuItem>
        )}
        {deliveredCount > 5 && (
          <>
            {cancelledCount > 0 && <DropdownMenuSeparator className="bg-[#2A0F3D]" />}
            <DropdownMenuItem
              onSelect={() => onDeleteOldDelivered?.()}
              className="cursor-pointer hover:bg-[rgba(255,59,48,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5 text-[#FF3B30]"
              data-testid="bulk-delete-old-delivered"
              title="Permanently delete delivered orders older than 7 days (no archive, no undo)"
            >
              <Trash2 className="w-3.5 h-3.5" />
              DELETE OLD DELIVERED
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
